import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { startPath, visibleNavigation } from '@/app/navigation'
import { Icon, SECTION_ICONS } from '@/components/Icon'
import '@/styles/errors.css'

/**
 * Общие части страниц «Нет доступа» и «Не найдено» (Err403.dc.html, Err404.dc.html).
 *
 * Обе страницы отвечают на один вопрос — «куда теперь» — и обе обязаны предлагать только
 * то, что откроется: ссылка на раздел без права вела бы из одного отказа в другой.
 */

/** Путь, по которому пришли: его называют в тексте и в строке для поддержки. */
export function useRequestedPath(): string {
  const location = useLocation()
  return location.pathname + location.search
}

/** «На старт» и «Назад» — две кнопки внизу обеих страниц. */
export function ErrorActions({ primary }: { primary?: boolean }) {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const home = startPath(user)
  return (
    <span className="sa err-actions">
      {home !== '/no-access' && (
        <Link to={home} className={primary ? 'btn btn-primary' : 'btn btn-secondary'}>
          {t('err.home')}
        </Link>
      )}
      <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
        <Icon name="arrow-left" size="sm" />
        {t('err.back')}
      </button>
    </span>
  )
}

/**
 * Строка для техподдержки: код, момент и путь — то, по чему находят запрос в логах.
 *
 * Номер запроса сюда не попадает: у навигации на клиенте его нет, а придуманный номер,
 * который не найдётся ни в одном логе, хуже отсутствующего.
 */
export function SupportCode({ code, path }: { code: number; path: string }) {
  const { t } = useApp()
  const [copied, setCopied] = useState(false)
  const [moment] = useState(() => new Date())
  const text = `${code} · ${moment.toLocaleDateString('ru-RU')} ${moment.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })} · ${path}`
  return (
    <div className="err-meta err-support">
      <span className="code">{t('err.support', { code: text })}</span>
      <button
        type="button"
        className="btn btn-ghost btn-sm btn-icon"
        aria-label={copied ? t('err.copied') : t('err.copy')}
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(() => setCopied(true), () => setCopied(false))
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} size="xs" />
      </button>
    </div>
  )
}

/**
 * Основные разделы — только доступные пользователю.
 *
 * Берутся из той же навигации, что меню, и теми же правами: отдельный список дал бы ссылку
 * на раздел, которого в меню нет, то есть в ещё один отказ.
 */
export function QuickLinks({ limit = 6 }: { limit?: number }) {
  const { t, user } = useApp()
  const items = visibleNavigation(user)
    .flatMap((section) => section.items.map((item) => ({ item, icon: SECTION_ICONS[section.titleKey] ?? 'file' })))
    .slice(0, limit)
  if (items.length === 0) return null
  return (
    <div className="stack err-block">
      <span className="overline">{t('err.sections')}</span>
      <nav className="quick-links" aria-label={t('err.sections')}>
        {items.map(({ item, icon }) => (
          <Link key={item.path} to={item.path}>
            <Icon name={icon} size="sm" className="muted" />
            <span>{t(item.labelKey)}</span>
          </Link>
        ))}
      </nav>
    </div>
  )
}
